import Dexie from 'dexie'
import ComponentState from './state'

const DB_KEY = 'ArticleComponents'

interface IComponentStateBackup {
  key: typeof DB_KEY
  siteId: number
  state: ComponentState
}

class ComponentBackupDatabase extends Dexie {
  /* キーに siteId、 value に編集中の記事情報を持つバックアップ */
  components: Dexie.Table<IComponentStateBackup, string>

  constructor() {
    super('ArticleComponents')
    this.version(1).stores({
      components: 'key, siteId, state'
    })
  }
}

class ComponentLocalStorage {

  get storedSiteId(): number | null {
    const value = localStorage.getItem('ContentEditor.storedSiteId')
    return value !== null ? parseInt(value, 10) : null
  }

  set storedSiteId(value: number | null) {
    if (value !== null) {
      localStorage.setItem('ContentEditor.storedSiteId', value.toString())
    } else {
      localStorage.removeItem('ContentEditor.storedSiteId')
    }
  }

  get hasUnsavedBackup(): boolean {
    return this.storedSiteId !== null
  }
}

export default class ComponentBackupStore {
  private db: ComponentBackupDatabase = new ComponentBackupDatabase()
  private localStorage: ComponentLocalStorage = new ComponentLocalStorage()

  async get(): Promise<ComponentState | undefined> {
    const backup = await this.getBackup()
    return (backup) ? backup.state : undefined
  }

  async getIfUnsaved(): Promise<ComponentState | undefined> {
    if (!this.localStorage.hasUnsavedBackup) {
      return undefined
    }
    const backup = await this.getBackup()
    return (backup && !backup.state.isContentSaved) ? backup.state : undefined
  }

  /**
   * @return {Promise<boolean>} - 未保存のバックアップがある場合、 true
   */
  storedSiteId(): number | null {
    return this.localStorage.storedSiteId
  }

  put(state: ComponentState): Promise<void> {
    const siteId = state.gerbera.siteId
    if (!siteId) {
      throw new Error('siteId がありません。')
    }
    if (state.isContentSaved) {
      // 記事が保存済みなので、バックアップは必要ないので削除する
      return this.remove()
    }
    return this.db.transaction('rw', this.db.components, async () => {
      return this.db.components.put({
        siteId,
        key: DB_KEY,
        state: Object.assign({}, state)
      })
    }).then(() => {
      // コンテンツ編集画面にいるかどうかを判定してからsiteID を代入
      const url = location.pathname
      const urlPattern = /^(\/app\/sites\/)(\d+)(\/contents\/)(\d+|new).*/
      const matched = url.match(urlPattern)
      if (matched) {
        this.localStorage.storedSiteId = siteId
      }
    })
  }

  remove(): Promise<void> {
    this.localStorage.storedSiteId = null
    return this.db.transaction('rw', this.db.components, async () => {
      return this.db.components.delete(DB_KEY)
    })
  }

  private async getBackup(): Promise<IComponentStateBackup | undefined> {
    return await this.db.components.get(DB_KEY)
  }
}
